/**
 * Safely parses a string into a JSON object.
 * @param text The string to parse.
 * @returns The parsed object or null if parsing fails.
 */
function tryParseJSON(text) {
    if (!text || typeof text !== 'string')
        return null;
    try {
        return JSON.parse(text);
    }
    catch (e) {
        return null;
    }
}
/**
 * Extracts a structured analysis object from the model's final 'content' string.
 * It handles cases where the content is a perfect JSON or embedded within text.
 * @param text The complete 'content' from the model.
 * @returns A `DeepSeekAnalysis` object.
 */
function extractJSONFromText(text) {
    if (!text || typeof text !== 'string') {
        return {
            directTranslation: 'Content is empty or invalid',
            contextualMeaning: '',
            grammarAnalysis: '',
        };
    }
    let parsed = tryParseJSON(text);
    if (parsed && typeof parsed === 'object' && 'directTranslation' in parsed) {
        return parsed;
    }
    const jsonStart = text.indexOf('{');
    const jsonEnd = text.lastIndexOf('}');
    if (jsonStart !== -1 && jsonEnd !== -1 && jsonEnd > jsonStart) {
        const potentialJSON = text.substring(jsonStart, jsonEnd + 1);
        parsed = tryParseJSON(potentialJSON);
        if (parsed && typeof parsed === 'object' && 'directTranslation' in parsed) {
            return parsed;
        }
    }
    console.warn('Failed to extract valid JSON from model content during client-side parsing.');
    return {
        directTranslation: 'Could not parse JSON',
        contextualMeaning: text.substring(0, 200) + (text.length > 200 ? '...' : ''),
        grammarAnalysis: 'Returned content is not in standard JSON format',
    };
}
/**
 * Parses a raw ReadableStream from the DeepSeek API.
 *
 * This async generator function reads the SSE stream, yielding text chunks for real-time UI updates,
 * and finally yields a complete `TranslationResponse` object upon stream completion.
 * It transparently handles both 'reasoner' (with `reasoning_content`) and 'chat' models.
 *
 * @param rawBody The ReadableStream from the `fetch` response.
 * @param originalText The original text that was sent for translation.
 * @param provider The `TranslationProvider` used for this request.
 * @returns An async iterable that yields strings (for UI) and a final TranslationResponse object.
 */
export async function* parseDeepSeekStream(rawBody, originalText, provider) {
    const reader = rawBody.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let accumulatedReasoningContent = '';
    let accumulatedContent = '';
    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                break;
            }
            buffer += decoder.decode(value, { stream: true });
            let boundaryIndex = buffer.indexOf('\n\n');
            while (boundaryIndex !== -1) {
                const eventString = buffer.substring(0, boundaryIndex);
                buffer = buffer.substring(boundaryIndex + 2);
                if (eventString.startsWith('data: ')) {
                    const jsonStr = eventString.substring(6).trim();
                    if (jsonStr === '[DONE]') {
                        // End of stream signal from the API. We break the inner loop
                        // and the outer loop will break on the next `reader.read()` returning `done: true`.
                        // This is a failsafe; the `done` condition should be the primary exit.
                        break;
                    }
                    try {
                        const jsonData = tryParseJSON(jsonStr);
                        const delta = jsonData?.choices?.[0]?.delta;
                        if (!delta) {
                            continue;
                        }
                        const reasoningChunk = delta.reasoning_content;
                        const contentChunk = delta.content;
                        if (reasoningChunk) {
                            accumulatedReasoningContent += reasoningChunk;
                            yield reasoningChunk;
                        }
                        if (contentChunk) {
                            accumulatedContent += contentChunk;
                            yield contentChunk;
                        }
                    }
                    catch (e) {
                        console.warn('[StreamParser] Error processing SSE JSON chunk:', e, '| Raw data:', jsonStr);
                    }
                }
                boundaryIndex = buffer.indexOf('\n\n');
            }
        }
    }
    catch (error) {
        console.error('[StreamParser] Error reading from stream:', error);
        throw new Error('Failed to read translation stream.');
    }
    finally {
        // FINALIZATION: This block executes when the stream is fully consumed or an error occurs.
        // Based on the accumulated content, build the final response object.
        const parsedAnalysis = extractJSONFromText(accumulatedContent);
        const bestTranslation = parsedAnalysis.contextualMeaning ||
            parsedAnalysis.directTranslation ||
            accumulatedContent.substring(0, 100);
        const finalResponse = {
            originalText: originalText,
            translatedText: bestTranslation,
            provider: provider, // Use the provider passed into the function
            analysis: {
                directTranslation: parsedAnalysis.directTranslation,
                contextualMeaning: parsedAnalysis.contextualMeaning,
                grammar: parsedAnalysis.grammarAnalysis,
            },
        };
        // Yield the final, complete object.
        yield finalResponse;
    }
}
/**
 * Parses a raw ReadableStream from the Gemini API, ensuring its behavior is
 * consistent with the parseDeepSeekStream implementation.
 *
 * It yields raw text chunks during the stream for real-time UI updates and
 * then yields a single, complete TranslationResponse object at the end.
 *
 * @param rawBody The ReadableStream from the `fetch` response.
 * @param originalText The original text that was sent for translation.
 * @param provider The `TranslationProvider` used for this request.
 * @returns An async iterable that yields strings and a final TranslationResponse object.
 */
export async function* parseGeminiStream(rawBody, originalText, provider) {
    const reader = rawBody.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let accumulatedJsonString = '';
    let accumulatedThoughts = ''; // 与 deepseek 保持一致，用于累加思考过程
    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) {
                break;
            }
            buffer += decoder.decode(value, { stream: true });
            let boundaryIndex = buffer.indexOf('\n\n');
            while (boundaryIndex !== -1) {
                const eventString = buffer.substring(0, boundaryIndex);
                buffer = buffer.substring(boundaryIndex + 2);
                if (eventString.startsWith('data: ')) {
                    const jsonStr = eventString.substring(6).trim();
                    if (jsonStr === '[DONE]') {
                        break;
                    }
                    try {
                        const jsonData = tryParseJSON(jsonStr);
                        const part = jsonData?.candidates?.[0]?.content?.parts?.[0];
                        if (!part?.text) {
                            continue;
                        }
                        // 1. 处理思考过程 (对应 deepseek 的 reasoning_content)
                        if (part.thought === true) {
                            accumulatedThoughts += part.text;
                            yield part.text;
                        }
                        // 2. 处理最终内容 (对应 deepseek 的 content)
                        if (part.thought !== true) {
                            accumulatedJsonString += part.text;
                            yield part.text;
                        }
                    }
                    catch (e) {
                        console.warn('[StreamParser] Error processing Gemini SSE JSON chunk:', e, '| Raw data:', jsonStr);
                    }
                }
                boundaryIndex = buffer.indexOf('\n\n');
            }
        }
    }
    catch (error) {
        console.error('[StreamParser] Error reading from Gemini stream:', error);
        throw new Error('Failed to read Gemini translation stream.');
    }
    finally {
        // FINALIZATION: Exactly like the deepseek parser, process the accumulated
        // string at the very end to create the final, structured object.
        const finalAnalysis = extractJSONFromText(accumulatedJsonString);
        const finalResponse = {
            originalText: originalText,
            translatedText: finalAnalysis.contextualMeaning || finalAnalysis.directTranslation || accumulatedJsonString,
            provider: provider,
            analysis: {
                directTranslation: finalAnalysis.directTranslation,
                contextualMeaning: finalAnalysis.contextualMeaning,
                grammar: finalAnalysis.grammarAnalysis,
            },
        };
        // Yield the final, complete object.
        yield finalResponse;
    }
}
//# sourceMappingURL=parser.js.map